import React, { useRef, useEffect, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import ChartBase from './ChartBase';
import { resolveChartData, getItemValue, getMaxValue } from './ChartData';
import '../../styles/chart-styles.css';

const AreaChart = ({ 
  data, 
  moduleId, 
  dataIndex, 
  title, 
  subtitle,
  showLabels = true, 
  animate = true,
  fillColor = null,
  opacity = 0.3,
  gradient = false,
  className = '',
  ...props 
}) => {
  const { getModuleData } = useTheme();
  const svgRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 100, height: 40 });
  
  // Get the entire module data array
  const moduleData = getModuleData(moduleId);
  const chartArray = resolveChartData(data, moduleData);
  const maxValue = getMaxValue(chartArray);

  // Handle resize events
  useEffect(() => {
    const handleResize = () => {
      if (svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        setDimensions({ width: rect.width, height: rect.height });
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <ChartBase title={title} subtitle={subtitle}>
      <AreaChartRenderer 
        chartArray={chartArray}
        maxValue={maxValue}
        dimensions={dimensions}
        svgRef={svgRef}
        opacity={opacity}
      />
    </ChartBase>
  );
};

// Internal AreaChart rendering logic
const AreaChartRenderer = ({ chartArray, maxValue, dimensions, svgRef, opacity }) => {
  // Use actual data instead of random values
  const points = chartArray.map(item => getItemValue(item));
  const maxPoint = Math.max(...points);
  
  // Use dynamic dimensions
  const svgWidth = dimensions.width || 100;
  const svgHeight = dimensions.height || 40;
  const padding = 5;
  
  // Calculate positions based on actual container dimensions
  const xStep = (svgWidth - 2 * padding) / (points.length - 1);
  const yScale = (svgHeight - 2 * padding) / maxPoint;
  
  // Create area path with actual data
  const areaPath = points.map((p, index) => {
    const x = padding + index * xStep;
    const y = svgHeight - padding - (p * yScale);
    return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
  }).join(' ');

  return (
    <div className="chart--area w-full theme-bg rounded-lg shadow-md p-4">
      {/* Chart Content */}
      <div className="h-40 w-full">
        <svg 
          ref={svgRef}
          viewBox={`0 0 ${svgWidth} ${svgHeight}`} 
          className="w-full h-full"
        >
          <defs>
            <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="var(--theme-primary)" stopOpacity={opacity} />
              <stop offset="100%" stopColor="var(--theme-primary)" stopOpacity={0.1} />
            </linearGradient>
          </defs>
          <path
            d={`${areaPath} L ${svgWidth - padding} ${svgHeight - padding} L ${padding} ${svgHeight - padding} Z`}
            fill="url(#areaGradient)"
            stroke="var(--theme-primary)"
            strokeWidth="2"
          />
          <polyline
            fill="none"
            stroke="var(--theme-primary)"
            strokeWidth="2"
            points={points.map((p, index) => {
              const x = padding + index * xStep;
              const y = svgHeight - padding - (p * yScale);
              return `${x},${y}`;
            }).join(' ')}
          />
        </svg>
      </div>
    </div>
  );
};

export default AreaChart;
